import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Shield, Play, Square, Loader2, LogOut, Users, MousePointerClick, DollarSign, Clock, Landmark, Check, X, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import AuthModal from "@/components/AuthModal";

function formatDur(secs) {
  if (secs <= 0) return "00d 00h 00m 00s";
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return `${String(d).padStart(2, "0")}d ${String(h).padStart(2, "0")}h ${String(m).padStart(2, "0")}m ${String(s).padStart(2, "0")}s`;
}

export default function Admin() {
  const navigate = useNavigate();
  const { user, loading: authLoading, logout: authLogout } = useAuth();
  const [state, setState] = useState(null);
  const [pendingTransfers, setPendingTransfers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [showLogin, setShowLogin] = useState(false);

  const isAdmin = user?.is_admin === true;

  const fetchState = async () => {
    if (!isAdmin) return;
    try {
      const [s, p] = await Promise.all([
        api.get("/admin/state"),
        api.get("/admin/pending-bank-transfers"),
      ]);
      setState(s.data);
      setCountdown(s.data.seconds_remaining || 0);
      setPendingTransfers(p.data || []);
    } catch (e) {
      if (e.response?.status === 401) toast.error("Sesión admin inválida");
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    fetchState();
    const iv = setInterval(fetchState, 5000);
    const tick = setInterval(() => setCountdown((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => { clearInterval(iv); clearInterval(tick); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const startContest = async () => {
    if (!window.confirm("¿Iniciar un NUEVO concurso de 72h? Se archivarán los perfiles actuales.")) return;
    setLoading(true);
    try {
      await api.post("/admin/start-contest");
      toast.success("Concurso iniciado — 72 horas");
      fetchState();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  };

  const endContest = async () => {
    if (!window.confirm("¿Cerrar concurso AHORA y ejecutar payouts?")) return;
    setLoading(true);
    try {
      await api.post("/admin/end-contest-now");
      toast.success("Concurso cerrado y resuelto");
      fetchState();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  };

  const approveTransfer = async (ref) => {
    if (!window.confirm(`¿Aprobar transferencia ${ref} y aplicar la puja?`)) return;
    try {
      await api.post(`/admin/pending-bank-transfers/${ref}/approve`);
      toast.success("Transferencia aprobada");
      fetchState();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const rejectTransfer = async (ref) => {
    if (!window.confirm(`¿Rechazar transferencia ${ref}?`)) return;
    try {
      await api.post(`/admin/pending-bank-transfers/${ref}/reject`);
      toast.info("Transferencia rechazada");
      fetchState();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const logout = async () => {
    await authLogout();
    navigate("/");
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-neon-cyan" />
      </div>
    );
  }

  // NOT logged in OR logged in as non-admin
  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="w-full max-w-md rounded-2xl border border-white/10 bg-void-card p-8 text-center">
          <div className="w-14 h-14 rounded-2xl bg-neon-cyan/15 border border-neon-cyan/40 flex items-center justify-center text-neon-cyan mx-auto mb-4">
            <Shield className="w-7 h-7" />
          </div>
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-neon-cyan mb-2">ADMIN PANEL</div>
          <h1 className="font-heading text-2xl font-black mb-3">Acceso restringido</h1>
          {user ? (
            <>
              <p className="text-sm text-muted-foreground mb-6">
                La cuenta <span className="text-white font-mono">{user.phone || user.email}</span> no tiene privilegios de administrador.
              </p>
              <div className="flex flex-col gap-2">
                <Button data-testid="admin-logout-and-relogin" onClick={logout} variant="outline" className="border-white/15 rounded-full">
                  Cerrar sesión y usar cuenta admin
                </Button>
                <Link to="/" className="text-xs font-mono text-muted-foreground hover:text-neon-cyan uppercase tracking-widest">
                  ← Volver al sitio
                </Link>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground mb-6">
                Ingresa con las credenciales de administrador (celular + contraseña).
              </p>
              <Button
                data-testid="admin-open-login-button"
                onClick={() => setShowLogin(true)}
                className="w-full bg-neon-cyan hover:bg-neon-cyan/90 text-black font-bold rounded-full py-6"
              >
                <Shield className="w-4 h-4 mr-2" /> Iniciar sesión como admin
              </Button>
              <Link to="/" className="block mt-4 text-xs font-mono text-muted-foreground hover:text-neon-cyan uppercase tracking-widest">
                ← Volver al sitio
              </Link>
            </>
          )}
          <AuthModal open={showLogin} onClose={() => setShowLogin(false)} />
        </div>
      </div>
    );
  }

  const active = state?.contest_active;

  return (
    <div className="min-h-screen px-4 sm:px-6 py-8 max-w-4xl mx-auto">
      <header className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-neon-cyan/15 border border-neon-cyan/40 flex items-center justify-center text-neon-cyan">
            <Shield className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-neon-cyan mb-1">
              ADMIN PANEL · <span className="text-neon-red">{user.phone}</span>
            </div>
            <h1 className="font-heading text-2xl font-black">FameWar Control</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/" className="text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-neon-cyan">
            <ArrowLeft className="w-3.5 h-3.5 inline mr-1" /> Sitio
          </Link>
          <Button variant="ghost" size="sm" onClick={logout} data-testid="admin-logout-button">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </header>

      <div data-testid="admin-contest-state" className={`rounded-2xl border-2 p-6 sm:p-8 mb-6 ${active ? "border-[#25D366]/50" : "border-white/10"}`}
        style={{ background: active ? "rgba(37,211,102,0.05)" : "#0A0A0A" }}>
        <div className="flex items-center gap-2 mb-4">
          <span className={`w-2 h-2 rounded-full ${active ? "bg-[#25D366]" : "bg-white/30"} ${active ? "animate-ticker-blink" : ""}`} />
          <span className="text-[10px] font-mono uppercase tracking-[0.3em]" style={{ color: active ? "#25D366" : "#888" }}>
            {active ? "CONCURSO ACTIVO" : "SIN CONCURSO ACTIVO"}
          </span>
        </div>
        {active ? (
          <>
            <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-1">
              Tiempo restante (72h total)
            </div>
            <div data-testid="admin-countdown" className="font-mono font-black tabular-nums leading-none" style={{ fontSize: "clamp(2.5rem, 6vw, 4.5rem)", color: "#25D366" }}>
              {formatDur(countdown)}
            </div>
            <div className="mt-2 text-xs font-mono text-muted-foreground">
              <Clock className="inline w-3 h-3 mr-1" /> Cierre: {state.closing_time?.replace("T", " ").slice(0, 19)} UTC
            </div>
          </>
        ) : (
          <div>
            <p className="text-lg font-heading font-bold mb-1">No hay concurso corriendo.</p>
            <p className="text-sm text-muted-foreground">Pulsa "Iniciar concurso" para lanzar 72 horas.</p>
          </div>
        )}
        <div className="mt-6 flex flex-wrap gap-3">
          <Button data-testid="admin-start-button" disabled={loading || active} onClick={startContest}
            className="bg-[#25D366] hover:bg-[#20BD5A] text-black font-bold rounded-full px-6">
            <Play className="w-4 h-4 mr-2" /> Iniciar concurso 72h
          </Button>
          <Button data-testid="admin-end-button" disabled={loading || !active} onClick={endContest}
            variant="outline" className="border-neon-red/60 text-neon-red hover:bg-neon-red/10 hover:text-neon-red font-bold rounded-full px-6">
            <Square className="w-4 h-4 mr-2" /> Forzar cierre ahora
          </Button>
        </div>
      </div>

      {state && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatBox testid="admin-stat-pool" label="Pool acumulado" value={`$${Number(state.total_pool_usd || 0).toFixed(2)}`} icon={DollarSign} color="#FAFF00" />
          <StatBox testid="admin-stat-profiles" label="Perfiles activos" value={state.profiles_count || 0} icon={Users} color="#00F0FF" />
          <StatBox testid="admin-stat-hunters" label="Hunters" value={state.hunters_count || 0} icon={Users} color="#FF003C" />
          <StatBox testid="admin-stat-clicks" label="Referral clicks hoy" value={state.referral_clicks_today || 0} icon={MousePointerClick} color="#25D366" />
        </div>
      )}

      <div className="mt-8" data-testid="pending-transfers-section">
        <div className="flex items-center gap-2 mb-4">
          <Landmark className="w-4 h-4 text-neon-gold" />
          <h2 className="font-heading text-lg font-bold">
            Transferencias pendientes ({pendingTransfers.length})
          </h2>
        </div>
        {pendingTransfers.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-white/10 p-8 text-center text-sm text-muted-foreground font-mono">
            No hay transferencias bancarias pendientes.
          </div>
        ) : (
          <div className="space-y-3">
            {pendingTransfers.map((t) => (
              <div key={t.session_id} data-testid={`pending-transfer-${t.session_id}`}
                className="rounded-xl border border-neon-gold/30 bg-void-card p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono font-bold text-neon-gold">{t.session_id}</span>
                    <span className="text-xs font-mono text-muted-foreground">· {t.tier} · ${Number(t.amount).toFixed(2)}</span>
                  </div>
                  <div className="text-xs font-mono text-muted-foreground">
                    {t.metadata?.handle_name || "(outbid)"} · {t.created_at_dt?.replace("T", " ").slice(0, 19)} UTC
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button data-testid={`approve-${t.session_id}`} size="sm" onClick={() => approveTransfer(t.session_id)}
                    className="bg-[#25D366] hover:bg-[#20BD5A] text-black font-bold rounded-full">
                    <Check className="w-4 h-4 mr-1" /> Aprobar
                  </Button>
                  <Button data-testid={`reject-${t.session_id}`} size="sm" variant="outline" onClick={() => rejectTransfer(t.session_id)}
                    className="border-neon-red/60 text-neon-red hover:bg-neon-red/10 rounded-full">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatBox({ label, value, icon: Icon, color, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-white/5 bg-void-card p-4">
      <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-3">
        <Icon className="w-3 h-3" style={{ color }} /> {label}
      </div>
      <div className="font-mono text-2xl font-bold tabular-nums" style={{ color }}>{value}</div>
    </div>
  );
}
